// src/pages/security-roles/SecurityRolePage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, Button, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import PaginatedTable from "../Common/PaginatedTable";
import EntityDetailsSidebar from "../Common/EntityDetailsSidebar";
import { formatDateTime } from "../../utils/formatters";
import {
  getPagedRoles,
  deleteRole,
  getUsersByRoleId,
} from "../../services/securityRoleService";
import { Pencil, Trash2, Eye, ShieldCheck } from "lucide-react";

import AlertMessage from "../Common/AlertMessage";

import extractApiErrors from "../../utils/extractApiErrors";

import UsersByRoleModal from "./components/UsersByRoleModal";
import PermissionsModal from "./components/PermissionsModal";

const SecurityRolePage = () => {
  const navigate = useNavigate();

  // Estado de tabla y UI
  const [roles, setRoles] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Estado de detalles / modales
  const [selectedRole, setSelectedRole] = useState(null);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersForRole, setUsersForRole] = useState([]);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsForRole, setPermissionsForRole] = useState([]);

  // Errores para operaciones (p.ej. eliminar) — se muestran en AlertMessage
  const [deleteErrors, setDeleteErrors] = useState([]);

  // Definición de campos del panel lateral
  const schema = [
    { label: "Nombre", key: "name" },
    { label: "Descripción", key: "description" },
    {
      label: "Permisos",
      key: "permissionDescriptions",
      render: (list) => list?.join(", ") || "Sin permisos",
    },
    { label: "Creado", key: "createdAt", render: formatDateTime },
    { label: "Actualizado", key: "updatedAt", render: formatDateTime },
  ];

  // Carga paginada con indicadores de carga y manejo de errores
  const fetchRoles = useCallback(async (page, size, search = "") => {
    try {
      setIsLoading(true);
      const response = await getPagedRoles(page, size, search);
      if (response.success) {
        setRoles(response.data.items);
        setTotalRows(response.data.totalCount);
      } else {
        throw new Error(response.message || "No se pudo cargar");
      }
    } catch (err) {
      Swal.fire("Error", "No se pudieron cargar los roles", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounce básico para búsquedas (300ms)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchRoles(currentPage, perPage, searchTerm);
    }, 300);
    return () => clearTimeout(t);
  }, [fetchRoles, currentPage, perPage, searchTerm]);

  // Navegación a edición
  const handleEdit = (id) => navigate(`/dashboard/security-role/edit/${id}`);

  // Eliminación con confirmación y captura de mensajes de la API
  const handleDelete = async (id) => {
    // Limpiar errores anteriores al intentar una nueva acción
    setDeleteErrors([]);

    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará el rol.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await deleteRole(id);

      if (!response?.success) {
        // El backend dijo que NO — propagamos su payload para parsearlo
        throw { backend: response };
      }

      await Swal.fire("Eliminado", "El rol ha sido eliminado.", "success");
      fetchRoles(currentPage, perPage, searchTerm);
    } catch (err) {
      //Igual que en tus formularios: parseamos y mostramos errores reales del backend
      const parsed = extractApiErrors(err);
      // setDeleteErrors(parsed);

      // Opcional: además del alert en la página, puedes reflejar el 1er error en un modal
      Swal.fire(
        "Información",
        parsed[0] || "No se pudo eliminar el rol.",
        "info"
      );
    }
  };

  // Búsqueda: resetea a la primera página
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // Columnas de la tabla
  const columns = [
    { name: "Nombre", selector: (row) => row.name, sortable: true, wrap: true },
    { name: "Descripción", selector: (row) => row.description, wrap: true },
    {
      name: "Permisos",
      cell: (row) => (
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setPermissionsForRole(row.permissionDescriptions || []);
            setShowPermissionsModal(true);
          }}
        >
          Ver permisos
        </Button>
      ),
      wrap: true,
      center: true,
    },
    {
      name: "Usuarios",
      cell: (row) => (
        <Button
          variant="outline-info"
          size="sm"
          onClick={async (e) => {
            e.stopPropagation();
            const response = await getUsersByRoleId(row.id);
            if (response.success) {
              setUsersForRole(response.data || []);
              setShowUsersModal(true);
            } else {
              Swal.fire("Error", "No se pudieron cargar los usuarios", "error");
            }
          }}
        >
          Ver usuarios
        </Button>
      ),
      wrap: true,
      center: true,
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.id);
            }}
            title="Editar"
          >
            <Pencil size={16} />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row.id);
            }}
            title="Eliminar"
          >
            <Trash2 size={16} />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRole(row);
            }}
            title="Ver detalles"
          >
            <Eye size={16} />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      wrap: true,
      width: "150px",
      center: true,
    },
  ];

  // Limpieza de estado al cerrar modales
  const closeUsersModal = () => {
    setUsersForRole([]);
    setShowUsersModal(false);
  };
  const closePermissionsModal = () => {
    setPermissionsForRole([]);
    setShowPermissionsModal(false);
  };

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 text-dark">Roles de seguridad</h4>
          <Button onClick={() => navigate("/dashboard/security-role/create")}>
            + Crear nuevo rol
          </Button>
        </div>

        {/* 🔔 Bloque de errores para operaciones (eliminar) */}
        {deleteErrors.length > 0 && (
          <div className="mb-3">
            <AlertMessage
              type="error"
              message={
                <ul className="mb-0">
                  {deleteErrors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              }
              onClose={() => setDeleteErrors([])}
            />
          </div>
        )}

        <PaginatedTable
          keyField="id"
          columns={columns}
          data={roles}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(newPerPage, page) => {
            setPerPage(newPerPage);
            setCurrentPage(page);
          }}
          onSearch={handleSearch}
          isLoading={isLoading}
        />

        {isLoading && (
          <div className="d-flex justify-content-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="text-muted">Cargando...</span>
          </div>
        )}
      </Card>

      <EntityDetailsSidebar
        isVisible={!!selectedRole}
        onClose={() => setSelectedRole(null)}
        title="Detalles del rol"
        icon={<ShieldCheck size={20} />}
        data={selectedRole}
        schema={schema}
      />

      <PermissionsModal
        show={showPermissionsModal}
        onHide={closePermissionsModal}
        permissions={permissionsForRole}
      />

      <UsersByRoleModal
        show={showUsersModal}
        onHide={closeUsersModal}
        users={usersForRole}
      />
    </div>
  );
};

export default SecurityRolePage;
